// src/layouts/StudentLayout.tsx
// Independent layout shell for the Student Portal.
import { useState } from 'react'
import { Outlet, NavLink } from 'react-router-dom'
import { BookOpen, Calendar, LogOut, Menu, X, User } from 'lucide-react'
import { useAuth } from '@/app/hooks'
import { useLogoutMutation } from '@/features/auth/auth.api'
import logo from '@/assets/logo2.png'

export default function StudentLayout() {
  const { user } = useAuth()
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await logout().unwrap()
    } catch {
      // Ignore network errors and proceed with local signout
    } finally {
      window.location.href = '/login'
    }
  }

  const studentName = user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email?.split('@')[0] || 'Student'
  const initials = studentName.slice(0, 2).toUpperCase()

  const navLinks = [
    { to: '/student/courses', label: 'My Courses & Study', icon: BookOpen },
    { to: '/student/exams', label: 'Exam Schedule', icon: Calendar },
  ]

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC] dark:bg-[#070b0e] text-neutral-900 dark:text-neutral-100 font-sans transition-colors duration-150">
      {/* ── Top Navigation Bar ────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 w-full border-b border-neutral-200/80 dark:border-neutral-800/80 bg-white/85 dark:bg-[#0a0f14]/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-[70px]">
            {/* Left: Brand Logo */}
            <div className="flex items-center gap-3 sm:gap-6">
              <NavLink to="/student/courses" className="flex items-center group">
                <img
                  src={logo}
                  alt="Dualon Logo"
                  className="h-11 sm:h-14 md:h-16 w-auto object-contain transition-transform group-hover:scale-105"
                />
              </NavLink>

              {/* Desktop Nav Items */}
              <nav className="hidden md:flex items-center gap-1.5 ml-4">
                {navLinks.map((link) => {
                  const Icon = link.icon
                  return (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className={({ isActive }) => `
                        flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium transition-all duration-150
                        ${isActive
                          ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-semibold shadow-xs'
                          : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800/60'
                        }
                      `}
                    >
                      <Icon size={17} className="stroke-[2.2]" />
                      <span>{link.label}</span>
                    </NavLink>
                  )
                })}
              </nav>
            </div>

            {/* Right: User Profile & Logout */}
            <div className="hidden md:flex items-center gap-3">
              {user?.classStandard && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-neutral-100/90 dark:bg-neutral-800/60 border border-neutral-200/80 dark:border-neutral-700/60 shadow-2xs select-none">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                    Class
                  </span>
                  <span className="px-1.5 py-0.5 rounded-md bg-emerald-500/15 dark:bg-emerald-500/25 text-emerald-700 dark:text-emerald-300 text-xs font-bold leading-none">
                    {user.classStandard}
                  </span>
                </div>
              )}

              {/* User Avatar & Name (Links to Profile) */}
              <NavLink
                to="/student/profile"
                title="View Profile"
                className={({ isActive }) => `
                  flex items-center gap-2.5 pl-2.5 py-1 pr-2 rounded-xl transition-all duration-150 border-l border-neutral-200 dark:border-neutral-800
                  ${isActive
                    ? 'bg-emerald-50/80 dark:bg-emerald-500/10 ring-1 ring-emerald-500/20'
                    : 'hover:bg-neutral-100/70 dark:hover:bg-neutral-800/50'
                  }
                `}
              >
                <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-400 flex items-center justify-center text-white text-xs font-bold shadow-xs shrink-0">
                  {initials}
                </div>
                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 max-w-[150px] truncate">
                  {studentName}
                </span>
              </NavLink>

              {/* Logout Button */}
              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                title="Sign out"
                className="p-2 rounded-xl text-neutral-500 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors ml-1 cursor-pointer disabled:opacity-50"
              >
                <LogOut size={18} />
              </button>
            </div>

            {/* Mobile Hamburger Button */}
            <div className="flex md:hidden items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-xl text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#0a0f14] px-4 pt-3 pb-5 space-y-2">
            <NavLink
              to="/student/profile"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between p-3 rounded-xl bg-neutral-100/70 dark:bg-neutral-800/50 hover:bg-emerald-50/60 dark:hover:bg-emerald-500/10 transition-colors mb-3 group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-400 flex items-center justify-center text-white text-sm font-bold shrink-0">
                  {initials}
                </div>
                <div>
                  <p className="text-sm font-bold text-neutral-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                    {studentName}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {user?.classStandard ? `Class ${user.classStandard}` : 'Student Account'} • View Profile
                  </p>
                </div>
              </div>
              <User size={16} className="text-neutral-400 group-hover:text-emerald-600 transition-colors mr-1" />
            </NavLink>

            {navLinks.map((link) => {
              const Icon = link.icon
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors
                    ${isActive
                      ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-semibold'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    }
                  `}
                >
                  <Icon size={18} />
                  <span>{link.label}</span>
                </NavLink>
              )
            })}

            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
            >
              <LogOut size={18} />
              <span>Sign out</span>
            </button>
          </div>
        )}
      </header>

      {/* ── Main Content Area ─────────────────────────────────────────── */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Outlet />
      </main>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="border-t border-neutral-200 dark:border-neutral-800 py-6 text-center text-xs text-neutral-500 dark:text-neutral-400">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>&copy; {new Date().getFullYear()} Dualon EdTech. All rights reserved.</span>
          <span>Learning Portal</span>
        </div>
      </footer>
    </div>
  )
}
